import { Button } from '../components/admin/ui';
import { useEffect, useState } from 'react';
import { User, Shield, Filter } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, Profile } from '../lib/admin-api';
import { DataTable } from '../components/admin/DataTable';

const roleOptions = [
  { value: 'all', label: 'All Roles' },
  { value: 'customer', label: 'Customer' },
  { value: 'employee', label: 'Employee' },
  { value: 'agent', label: 'Agent' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
];

export function UsersPage() {
  const { profile: currentUser } = useAuth();
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [editingUser, setEditingUser] = useState<Profile | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!currentUser?.organization_id) return;

      setLoading(true);
      const data = await adminApi.getUsers(currentUser.organization_id);
      setUsers(data);
      setLoading(false);
    }

    fetchData();
  }, [currentUser?.organization_id]);

  const filteredUsers = roleFilter === 'all'
    ? users
    : users.filter((u) => u.role === roleFilter);

  const handleRoleUpdate = async (userId: string, newRole: string) => {
    const success = await adminApi.updateUser(userId, { role: newRole });
    if (success) {
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
      );
    }
    setEditingUser(null);
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      owner: 'bg-info text-info-foreground',
      admin: 'bg-primary/15 text-link',
      supervisor: 'bg-info text-info-foreground',
      agent: 'bg-success text-success-foreground',
      employee: 'bg-warning text-warning-foreground',
      customer: 'bg-muted text-foreground',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[role] || styles.customer}`}>
        {role}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-success text-success-foreground',
      away: 'bg-warning text-warning-foreground',
      busy: 'bg-destructive/10 text-error-text',
      offline: 'bg-muted text-foreground',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.offline}`}>
        {status}
      </span>
    );
  };

  const columns = [
    {
      key: 'display_name',
      header: 'User',
      render: (user: Profile) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/15 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-link" />
          </div>
          <div>
            <p className="font-medium text-foreground">
              {user.display_name || 'Unnamed User'}
            </p>
            <p className="text-sm text-muted-foreground">{user.email || 'No email'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: Profile) => (
        editingUser?.id === user.id ? (
          <select
            value={user.role}
            onChange={(e) => handleRoleUpdate(user.id, e.target.value)}
            onBlur={() => setEditingUser(null)}
            autoFocus
            className="border border-border rounded px-2 py-1 text-sm"
          >
            {roleOptions.slice(1).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <Button variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setEditingUser(user);
            }}
            className="hover:opacity-75"
          >
            {getRoleBadge(user.role)}
          </Button>
        )
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: Profile) => getStatusBadge(user.status),
    },
    {
      key: 'department',
      header: 'Department',
      render: (user: Profile) => (
        <span className="text-muted-foreground">{user.department || '—'}</span>
      ),
    },
    {
      key: 'last_seen_at',
      header: 'Last Seen',
      render: (user: Profile) => (
        <span className="text-muted-foreground">
          {user.last_seen_at
            ? new Date(user.last_seen_at).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Joined',
      render: (user: Profile) => (
        <span className="text-muted-foreground">
          {new Date(user.created_at).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Users</h1>
          <p className="text-muted-foreground mt-1">Manage team members and their roles</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-card p-4 rounded-lg border border-border">
        <Filter className="w-5 h-5 text-muted-foreground" />
        <div className="flex items-center gap-2">
          <label htmlFor="userspage-1" className="text-sm font-medium text-foreground">Role:</label>
          <select id="userspage-1"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <span className="text-sm text-muted-foreground ml-auto">
          {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={filteredUsers}
        loading={loading}
        emptyMessage="No users found"
        keyExtractor={(user) => user.id}
      />

      <div className="bg-info border border-border rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-info-foreground mt-0.5" />
          <div>
            <p className="text-sm font-medium text-info-foreground">Role Permissions</p>
            <p className="text-sm text-info-foreground mt-1">
              Click on a role badge to change it. Owner and Admin have full access.
              Supervisors can view analytics and manage agents.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
